import React from "react";
import { ButtonBase, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { tokens } from "../../theme";
import type { MainSection } from "../header/headerDesktop";

const SECTIONS: { value: MainSection; label: string; icon: React.ReactNode }[] = [
  { value: "lpp", label: "LPP monitor", icon: <ShowChartRoundedIcon /> },
  { value: "reports", label: "Reports", icon: <ArticleOutlinedIcon /> },
  { value: "trading", label: "Trading", icon: <SwapHorizRoundedIcon /> },
];

/**
 * Sidebar sections as full rows (44px on touch); `children` (the strategy list)
 * follows them while LPP is active.
 */
export const SectionNav: React.FC<{
  value: MainSection;
  onChange: (section: MainSection) => void;
  children?: React.ReactNode;
}> = ({ value, onChange, children }) => (
  <Stack spacing={0}>
  <Stack component="nav" aria-label="Sections" spacing={0.25}>
    {SECTIONS.map((s) => {
      const active = s.value === value;
      return (
        <React.Fragment key={s.value}>
          <ButtonBase
            onClick={() => onChange(s.value)}
            aria-current={active ? "page" : undefined}
            sx={{
              width: "100%",
              justifyContent: "flex-start",
              gap: 1.25,
              px: 1.5,
              minHeight: 40,
              "@media (pointer: coarse)": { minHeight: 44 },
              borderRadius: "14px",
              color: active ? "text.primary" : "text.secondary",
              bgcolor: active ? "rgba(255,255,255,0.08)" : "transparent",
              boxShadow: active ? "inset 0 1px 0 rgba(255,255,255,0.08)" : "none",
              "& .MuiSvgIcon-root": { fontSize: 19, color: active ? tokens.goldLight : "inherit" },
              "&:hover": { bgcolor: active ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.05)", color: "text.primary" },
              "&.Mui-focusVisible": { outline: `2px solid ${alpha(tokens.gold, 0.7)}`, outlineOffset: 1 },
            }}
          >
            {s.icon}
            <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 650 }}>
              {s.label}
            </Typography>
          </ButtonBase>
        </React.Fragment>
      );
    })}
  </Stack>
  {/* Strategy list (LPP only), below the always-visible section rows */}
  {value === "lpp" && children ? (
    <Stack sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${tokens.borderSubtle}` }}>{children}</Stack>
  ) : null}
  </Stack>
);

export default SectionNav;
